import { Spin } from 'antd';

import { ScrollMinimap } from '@app/shared/ui';
import type { MinimapColumn } from '@app/shared/ui';

import { CalendarTimeline } from '../../CalendarTimeline';
import { CurrentTimeLine } from '../../CurrentTimeLine';
import { EmployeeBreakActionModal } from '../../EmployeeHeaderActions';
import { EmployeesHeadersStrip } from '../../EmployeesHeadersStrip';
import {
  EmployeesViewport,
  useEmployeesViewport,
} from '../../EmployeesViewport';
import { EmptyCalendarState } from '../../EmptyCalendarState';
import { useCalendarLayout } from '../hooks';

import type { CSSProperties, FC } from 'react';
import '../styles/CalendarLayout.scss';

type CalendarLayoutStyle = CSSProperties &
  Record<'--calendar-interval-height' | '--calendar-grid-step-height', string>;

// Matches the 1px inter-column gap of `.EmployeesViewport__track`.
const EMPLOYEE_COLUMN_GAP_PX = 1;

export const CalendarLayout: FC = () => {
  const {
    employees,
    events,
    isLoading,
    intervalHeightPx,
    gridStepHeightPx,
    startTime,
    endTime,
    boundaryEndTime,
    bookingStepMinutes,
    timeFormat,
    hasEmployees,
    isTodaySelected,
    selectedBreak,
    slotClickHandler,
    slotBreakClickHandler,
    breakClickHandler,
    closeBreakModalHandler,
    breakModalSuccessHandler,
    onEventUpdate,
    onRecordClick,
    canShowContacts,
    previewFields,
    slotMenu,
    openSlotMenuHandler,
    closeSlotMenuHandler,
  } = useCalendarLayout();

  const {
    viewportRef,
    columnWidth,
    totalTrackWidth,
    hasOverflow,
    isAtStart,
    isAtEnd,
    scrollToPrevPageHandler,
    scrollToNextPageHandler,
    trackTransform,
    wheelHandler,
    scrollModel,
    dragState,
    dragStateChangeHandler,
  } = useEmployeesViewport({
    totalEmployees: employees.length,
  });

  const minimapColumns: MinimapColumn[] = employees.map((employee, index) => ({
    id: employee.id,
    offset: index * (columnWidth + EMPLOYEE_COLUMN_GAP_PX),
    width: columnWidth,
  }));

  const layoutStyle: CalendarLayoutStyle = {
    '--calendar-interval-height': `${intervalHeightPx}px`,
    '--calendar-grid-step-height': `${gridStepHeightPx}px`,
  };

  if (isLoading) {
    return (
      <div className="CalendarLayout" data-testid="calendar-layout">
        <div className="CalendarLayout__empty">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!hasEmployees) {
    return (
      <div className="CalendarLayout" data-testid="calendar-layout">
        <div className="CalendarLayout__empty">
          <EmptyCalendarState />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="CalendarLayout"
        style={layoutStyle}
        data-testid="calendar-layout"
      >
        <div className="CalendarLayout__scroll-area">
          <CurrentTimeLine
            startTime={startTime}
            isToday={isTodaySelected}
            intervalHeightPx={intervalHeightPx}
          />

          <div className="CalendarLayout__timeline CalendarLayout__timeline--left">
            <CalendarTimeline
              side="left"
              startTime={startTime}
              endTime={endTime}
              boundaryEndTime={boundaryEndTime}
              stepMinutes={bookingStepMinutes}
              timeFormat={timeFormat}
              onNavigate={scrollToPrevPageHandler}
              disabled={isAtStart}
            />
          </div>

          <div className="CalendarLayout__center">
            <EmployeesHeadersStrip
              employees={employees}
              columnWidth={columnWidth}
              totalTrackWidth={totalTrackWidth}
              hasOverflow={hasOverflow}
              trackTransform={trackTransform}
              onWheel={wheelHandler}
            />
            <EmployeesViewport
              ref={viewportRef}
              employees={employees}
              events={events}
              startTime={startTime}
              endTime={endTime}
              bookingStepMinutes={bookingStepMinutes}
              timeFormat={timeFormat}
              intervalHeightPx={intervalHeightPx}
              columnWidth={columnWidth}
              totalTrackWidth={totalTrackWidth}
              hasOverflow={hasOverflow}
              trackTransform={trackTransform}
              onWheel={wheelHandler}
              dragState={dragState}
              dragStateChangeHandler={dragStateChangeHandler}
              onSlotClick={slotClickHandler}
              onSlotBreakClick={slotBreakClickHandler}
              onBreakClick={breakClickHandler}
              onEventUpdate={onEventUpdate}
              onRecordClick={onRecordClick}
              canShowContacts={canShowContacts}
              previewFields={previewFields}
              activeSlotMenu={slotMenu}
              onSlotMenuOpen={openSlotMenuHandler}
              onSlotMenuClose={closeSlotMenuHandler}
            />
          </div>

          <div className="CalendarLayout__timeline CalendarLayout__timeline--right">
            <CalendarTimeline
              side="right"
              startTime={startTime}
              endTime={endTime}
              boundaryEndTime={boundaryEndTime}
              stepMinutes={bookingStepMinutes}
              timeFormat={timeFormat}
              onNavigate={scrollToNextPageHandler}
              disabled={isAtEnd}
            />
          </div>
        </div>
        {hasOverflow && (
          <ScrollMinimap
            className="CalendarLayout__minimap"
            model={scrollModel}
            columns={minimapColumns}
          />
        )}
      </div>
      {selectedBreak && (
        <EmployeeBreakActionModal
          breakId={selectedBreak.id ?? undefined}
          date={selectedBreak.date}
          employeeId={selectedBreak.employeeId}
          employeeName={selectedBreak.employeeName}
          initialTimeS={selectedBreak.timeS}
          initialTimeE={selectedBreak.timeE}
          isOpen
          mode={selectedBreak.id === null ? 'create' : 'edit'}
          onClose={closeBreakModalHandler}
          onSuccess={breakModalSuccessHandler}
        />
      )}
    </>
  );
};
